/* ============================================
   Globdrop — UI Renderer
   
   Renders all screens using vanilla DOM manipulation.
   Each render function creates the DOM for a game state.
   ============================================ */

import type { Scenario, ScenarioIndex, GameMode, GameSession, RoundResult, CoordinateSystem } from '../types/scenario.ts';
import { formatPoints, formatDistance, getLevel, maxPossiblePoints } from '../engine/scoring.ts';
import { coordToPixel } from '../engine/coordinate-math.ts';
import {
  shareWhatsApp,
  shareFacebook,
  shareInstagram,
  shareNative,
  copyToClipboard,
  canNativeShare,
} from '../engine/share.ts';
import { t, getLocalized, setLanguage, getLanguage, LANGUAGES, Language } from '../engine/i18n.ts';
import type { ShareData } from '../types/scenario.ts';
import { pushAd } from '../utils/ads.ts';
import { track } from '../utils/analytics.ts';

// ── Helpers ──

function $(selector: string): HTMLElement {
  return document.querySelector(selector)!;
}

/**
 * Calculates the actual rendered dimensions and offsets of an image content 
 * within its <img> tag when object-fit: contain is used.
 */
function getRenderedImageInfo(img: HTMLImageElement) {
  const rect = img.getBoundingClientRect();
  const naturalRatio = img.naturalWidth / img.naturalHeight;
  const visibleRatio = rect.width / rect.height;

  let renderedWidth, renderedHeight, offsetX, offsetY;

  if (visibleRatio > naturalRatio) {
    // Height is the limiting factor (letterbox on sides)
    renderedHeight = rect.height;
    renderedWidth = renderedHeight * naturalRatio;
    offsetX = (rect.width - renderedWidth) / 2;
    offsetY = 0;
  } else {
    // Width is the limiting factor (letterbox on top/bottom)
    renderedWidth = rect.width;
    renderedHeight = renderedWidth / naturalRatio;
    offsetX = 0;
    offsetY = (rect.height - renderedHeight) / 2;
  }

  return { renderedWidth, renderedHeight, offsetX, offsetY };
}

function showToast(message: string): void {
  const toast = $('#toast');
  toast.textContent = message;
  toast.classList.add('visible');
  setTimeout(() => toast.classList.remove('visible'), 2500);
}

function clearMain(): void {
  const main = $('#main');
  main.innerHTML = '';
}

function createScreen(id: string): HTMLElement {
  const div = document.createElement('div');
  div.className = 'screen active';
  div.id = `screen-${id}`;
  return div;
}

// ── Screen: Scenario Selection (IDLE) ──

export function renderScenarioSelection(
  index: ScenarioIndex,
  onSelect: (scenarioId: string) => void
): void {
  clearMain();
  const screen = createScreen('scenarios');
  screen.classList.add('screen--wide');

  screen.innerHTML = `
    <div class="home-wrapper">
      <!-- Left Sidebar (Desktop) -->
      <div class="ad-sidebar ad-sidebar-left ad-container ad-stack">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
      </div>

      <div class="home-content">
        <div class="lang-switcher anim-fade-in">
          ${LANGUAGES.map(l => `
            <button class="lang-btn ${getLanguage() === l.code ? 'active' : ''}" data-lang="${l.code}">
              <span class="lang-btn__flag">${l.flag}</span>
              <span class="lang-btn__label">${l.label}</span>
            </button>
          `).join('')}
        </div>
        <img src="/logo.svg" alt="Globdrop Logo" class="hero-logo anim-float" />
        <h1 class="title-hero">${t('app.title')}</h1>
        <p class="subtitle">${t('app.subtitle')}</p>
        <div class="category-selectors anim-fade-in-up">
          <button class="category-card" id="tab-maps" data-tab="maps">
            <div class="category-card__glow" style="background: radial-gradient(circle, rgba(116, 185, 255, 0.12) 0%, transparent 70%);"></div>
            <div class="category-card__icon-wrapper">
              <span class="category-card__icon">🗺️</span>
            </div>
            <h2 class="category-card__title">${t('tabs.maps' as any) || 'Maps'}</h2>
            <p class="category-card__desc">${t('category.maps.desc' as any)}</p>
          </button>

          <button class="category-card" id="tab-flags" data-tab="flags">
            <div class="category-card__glow" style="background: radial-gradient(circle, rgba(249, 115, 22, 0.12) 0%, transparent 70%);"></div>
            <div class="category-card__icon-wrapper">
              <span class="category-card__icon">🏁</span>
            </div>
            <h2 class="category-card__title">${t('tabs.flags' as any) || 'Flags'}</h2>
            <p class="category-card__desc">${t('category.flags.desc' as any)}</p>
          </button>
        </div>
        <div class="scenario-grid stagger" id="scenario-grid"></div>
        
        <!-- Bottom Ad (Mobile) -->
        <div class="ad-bottom-mobile ad-container" style="margin-top: 2rem; width: 100%; text-align: center;">
          <ins class="adsbygoogle"
               style="display:block"
               data-ad-client="ca-pub-7741865405447617"
               data-ad-slot="5810682723"
               data-ad-format="auto"
               data-full-width-responsive="true"></ins>
        </div>
      </div>

      <!-- Right Sidebar (Desktop) -->
      <div class="ad-sidebar ad-sidebar-right ad-container ad-stack">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
      </div>
    </div>
  `;

  $('#main').appendChild(screen);
  
  // Push all sidebar and mobile ads
  for (let i = 0; i < 7; i++) {
    pushAd();
  }

  // Wire up language switcher
  screen.querySelectorAll('.lang-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const lang = btn.getAttribute('data-lang') as Language;
      setLanguage(lang);
      window.location.reload(); // Simple way to re-render everything with new language
    });
  });

  const tabMaps = screen.querySelector('#tab-maps') as HTMLButtonElement;
  const tabFlags = screen.querySelector('#tab-flags') as HTMLButtonElement;
  const grid = screen.querySelector('#scenario-grid')!;

  const setStoredTab = (tab: 'maps' | 'flags') => {
    try {
      localStorage.setItem('activeTab', tab);
    } catch {}
  };

  const renderGrid = (tab: 'maps' | 'flags' | null) => {
    grid.innerHTML = '';

    if (tab == null) {
      tabMaps.classList.remove('active');
      tabFlags.classList.remove('active');
      grid.innerHTML = `
        <div class="category-hint anim-fade-in-up">
          <span class="category-hint__emoji">✨</span>
          <p class="category-hint__text">${t('category.select_hint' as any)}</p>
        </div>
      `;
      return;
    }

    setStoredTab(tab);

    if (tab === 'maps') {
      tabMaps.classList.add('active');
      tabFlags.classList.remove('active');

      const mapScenarios = index.scenarios.filter((s: any) => !s.isFlags);

      // Random Scenario Card (I'm feeling lucky)
      const randomCard = document.createElement('button');
      randomCard.className = 'scenario-card card scenario-card--random';
      randomCard.id = 'scenario-random';
      randomCard.innerHTML = `
        <div class="scenario-card__icon">🎲</div>
        <h2 class="scenario-card__title">${t('scenario.random.title' as any)}</h2>
        <p class="scenario-card__desc">${t('scenario.random.desc' as any)}</p>
        <span class="scenario-card__count">? ${t('scenario.places')}</span>
      `;
      randomCard.style.background = 'linear-gradient(135deg, var(--color-primary), var(--color-accent))';
      randomCard.style.color = 'white';
      
      randomCard.addEventListener('click', () => {
        if (mapScenarios.length > 0) {
          const randomIndex = Math.floor(Math.random() * mapScenarios.length);
          onSelect(mapScenarios[randomIndex]!.id);
        }
      });
      grid.appendChild(randomCard);

      for (const s of mapScenarios) {
        const card = document.createElement('button');
        card.className = 'scenario-card card';
        card.id = `scenario-${s.id}`;
        card.innerHTML = `
          <div class="scenario-card__icon">${s.icon || '🗺️'}</div>
          <h2 class="scenario-card__title">${getLocalized(s.title)}</h2>
          <p class="scenario-card__desc">${getLocalized(s.description)}</p>
          <span class="scenario-card__count">${s.placeCount} ${t('scenario.places')}</span>
        `;
        card.addEventListener('click', () => onSelect(s.id));
        grid.appendChild(card);
      }

      // Add placeholders
      const placeholderCount = 3;
      for (let i = 0; i < placeholderCount; i++) {
        const placeholder = document.createElement('div');
        placeholder.className = 'scenario-card card scenario-card--placeholder scenario-card--stacked anim-fade-in-up';
        placeholder.style.animationDelay = `${(mapScenarios.length + i + 2) * 0.05}s`;
        placeholder.innerHTML = `
          <div class="scenario-card__icon" style="opacity: 0.15">🗺️</div>
          <h2 class="scenario-card__title" style="opacity: 0.25">${t('scenario.more_coming' as any)}</h2>
          <p class="scenario-card__desc" style="opacity: 0.15">${t('scenario.more_coming_desc' as any)}</p>
        `;
        grid.appendChild(placeholder);
      }
    } else {
      tabFlags.classList.add('active');
      tabMaps.classList.remove('active');

      const flagScenarios = index.scenarios.filter((s: any) => s.isFlags);

      // Random Flags Card (I'm feeling lucky)
      const randomFlagCard = document.createElement('button');
      randomFlagCard.className = 'scenario-card card scenario-card--random';
      randomFlagCard.id = 'scenario-random-flags';
      randomFlagCard.innerHTML = `
        <div class="scenario-card__icon">🎲</div>
        <h2 class="scenario-card__title">${t('scenario.random.title' as any)}</h2>
        <p class="scenario-card__desc">${t('scenario.random.desc' as any)}</p>
        <span class="scenario-card__count">? ${t('scenario.places')}</span>
      `;
      randomFlagCard.style.background = 'linear-gradient(135deg, var(--color-primary), var(--color-accent))';
      randomFlagCard.style.color = 'white';
      
      randomFlagCard.addEventListener('click', () => {
        if (flagScenarios.length > 0) {
          const randomIndex = Math.floor(Math.random() * flagScenarios.length);
          onSelect(flagScenarios[randomIndex]!.id);
        }
      });
      grid.appendChild(randomFlagCard);

      for (const s of flagScenarios) {
        const card = document.createElement('button');
        card.className = 'scenario-card card';
        card.id = `scenario-${s.id}`;
        card.innerHTML = `
          <div class="scenario-card__icon">${s.icon || '🏁'}</div>
          <h2 class="scenario-card__title">${getLocalized(s.title)}</h2>
          <p class="scenario-card__desc">${getLocalized(s.description)}</p>
          <span class="scenario-card__count">${s.placeCount} ${t('scenario.places')}</span>
        `;
        card.addEventListener('click', () => onSelect(s.id));
        grid.appendChild(card);
      }
    }
  };

  tabMaps.addEventListener('click', () => renderGrid('maps'));
  tabFlags.addEventListener('click', () => renderGrid('flags'));

  // Initialize with null so that no category is selected initially
  renderGrid(null);
}

// ── Screen: Mode Selection (SETUP) ──

export function renderModeSelection(
  scenario: Scenario,
  onSelect: (mode: GameMode) => void,
  onBack: () => void
): void {
  clearMain();
  const screen = createScreen('modes');

  screen.innerHTML = `
    <button class="btn-back anim-fade-in-up" id="btn-back">
      <svg viewBox="0 0 24 24" width="20" height="20"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
      <span>${t('mode.back')}</span>
    </button>
    <h1 class="title-section">${getLocalized(scenario.title)}</h1>
    <p class="subtitle">${getLocalized(scenario.description)}</p>
    <div class="mode-list stagger" id="mode-list"></div>
  `;

  $('#main').appendChild(screen);

  $('#btn-back').addEventListener('click', onBack);

  const list = screen.querySelector('#mode-list')!;
  for (const mode of scenario.modes) {
    const btn = document.createElement('button');
    btn.className = `btn btn--lg ${mode.colorClass} mode-btn`;
    btn.id = `mode-${mode.id}`;
    btn.innerHTML = `
      <span class="mode-btn__label">${getLocalized(mode.label)}</span>
      <span class="mode-btn__desc">${getLocalized(mode.description)}</span>
    `;
    btn.addEventListener('click', () => onSelect(mode));
    list.appendChild(btn);
  }
}

// ── Screen: Countdown (COUNTDOWN) ──

export function renderCountdown(
  session: GameSession,
  onDone: () => void
): void {
  clearMain();
  const screen = createScreen('countdown');

  screen.innerHTML = `
    <p class="round-indicator">${session.currentRound + 1} ${t('game.round')} ${session.totalRounds}</p>
    <p class="place-name" id="place-name">${getLocalized(session.currentPlace?.name) ?? ''}</p>
    <div class="countdown-number" id="countdown-number">3</div>
  `;

  $('#main').appendChild(screen);

  const numEl = screen.querySelector('#countdown-number') as HTMLElement;
  let count = 3;

  const interval = setInterval(() => {
    count--;
    if (count <= 0) {
      clearInterval(interval);
      onDone();
    } else {
      numEl.textContent = String(count);
      numEl.classList.remove('anim-countdown-tick');
      void numEl.offsetWidth; // Force reflow to restart animation
      numEl.classList.add('anim-countdown-tick');
    }
  }, 1000);
}

// ── Screen: Playing (PLAYING) ──

export function renderPlaying(
  session: GameSession,
  mapSrc: string,
  onMapClick: (pixel: { px: number; py: number }, imageSize: { width: number; height: number }) => void,
  onTimerTick: (updateFn: (remaining: number) => void) => void
): void {
  clearMain();
  const screen = createScreen('playing');
  screen.classList.add('screen--full');

  screen.innerHTML = `
    <div class="playing-wrapper">
      <div class="ad-sidebar ad-sidebar-left ad-container">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
      </div>
      <div class="game-container stagger">
        <div class="game-hud" id="hud">
        <div class="hud-left">
          <span class="hud-points" id="hud-points">${formatPoints(session.totalPoints)} ${t('game.points')}</span>
        </div>
        <div class="hud-center">
          <span class="hud-place" id="hud-place">${getLocalized(session.currentPlace?.name) ?? ''}</span>
        </div>
        <div class="hud-right">
          <div class="hud-timer" id="hud-timer">
            <svg class="timer-ring" viewBox="0 0 48 48">
              <circle class="timer-ring__bg" cx="24" cy="24" r="20" />
              <circle class="timer-ring__fg" id="timer-ring-fg" cx="24" cy="24" r="20" />
            </svg>
            <span class="timer-text" id="timer-text">${session.mode!.timePerRound}</span>
          </div>
        </div>
      </div>
      <div class="map-container" id="map-container">
        <img
          class="map-image ${session.scenario!.map.toLowerCase().includes('catalunya') ? 'map-image--white' : ''}"
          id="map-image"
          src="${mapSrc}"
          alt="${t('game.map_alt')}${getLocalized(session.scenario!.title)}"
          draggable="false"
        />
        <div class="pinpoint pinpoint--user hidden" id="pin-user">
          <svg viewBox="0 0 24 36" width="24" height="36"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#f97316"/><circle cx="12" cy="12" r="5" fill="white"/></svg>
        </div>
        <div class="pinpoint pinpoint--target hidden" id="pin-target">
          <svg viewBox="0 0 24 36" width="24" height="36"><path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 24 12 24s12-15 12-24C24 5.4 18.6 0 12 0z" fill="#fbbf24"/><circle cx="12" cy="12" r="5" fill="white"/></svg>
        </div>
        <svg class="distance-line hidden" id="distance-line"><line id="dist-line" stroke-width="3" stroke-dasharray="6,4"/></svg>
      </div>
      <div class="next-btn hidden" id="next-btn"></div>
      </div>
      <div class="ad-sidebar ad-sidebar-right ad-container">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="vertical"
             data-full-width-responsive="true"></ins>
      </div>
    </div>
  `;

  $('#main').appendChild(screen);
  pushAd();
  pushAd();

  // Map click handler
  const mapImg = screen.querySelector('#map-image') as HTMLImageElement;

  const handleClick = (e: PointerEvent) => {
    const rect = mapImg.getBoundingClientRect();
    const info = getRenderedImageInfo(mapImg);

    // Pixel relative to the rendered image content
    const px = e.clientX - rect.left - info.offsetX;
    const py = e.clientY - rect.top - info.offsetY;

    mapImg.removeEventListener('pointerdown', handleClick);
    mapImg.style.cursor = 'default';

    onMapClick(
      { px, py },
      { width: info.renderedWidth, height: info.renderedHeight }
    );
  };

  mapImg.addEventListener('pointerdown', handleClick);
  mapImg.style.cursor = 'crosshair';
  mapImg.style.touchAction = 'none';

  // Timer update
  const circumference = 2 * Math.PI * 20;
  const ringFg = screen.querySelector('#timer-ring-fg') as SVGCircleElement;
  ringFg.style.strokeDasharray = String(circumference);
  ringFg.style.strokeDashoffset = '0';

  const timerText = screen.querySelector('#timer-text') as HTMLElement;
  const maxTime = session.mode!.timePerRound;

  onTimerTick((remaining: number) => {
    timerText.textContent = String(Math.ceil(remaining));
    const progress = 1 - remaining / maxTime;
    ringFg.style.strokeDashoffset = String(circumference * progress);

    // Color change when low on time
    if (remaining <= 3) {
      ringFg.style.stroke = 'var(--color-danger)';
      timerText.style.color = 'var(--color-danger)';
    }
  });
}

/** Show pinpoints and distance line on the map after a click */
export function showFeedbackOnMap(
  userPixel: { px: number; py: number },
  targetCoord: { x: number; y: number },
  boundingBox: { topLeft: { x: number; y: number }; bottomRight: { x: number; y: number } },
  system: CoordinateSystem = 'WGS84'
): void {
  const mapContainer = $('#map-container');
  const mapImg = $('#map-image') as HTMLImageElement;
  const rect = mapImg.getBoundingClientRect();
  const containerRect = mapContainer.getBoundingClientRect();

  const info = getRenderedImageInfo(mapImg);
  const offsetX = rect.left - containerRect.left;
  const offsetY = rect.top - containerRect.top;

  const adjustedOffsetX = offsetX + info.offsetX;
  const adjustedOffsetY = offsetY + info.offsetY;

  // User pin
  const pinUser = $('#pin-user');
  pinUser.style.left = `${adjustedOffsetX + userPixel.px - 12}px`;
  pinUser.style.top = `${adjustedOffsetY + userPixel.py - 36}px`;
  pinUser.classList.remove('hidden');
  pinUser.classList.add('anim-pinpoint-drop');

  // Target pin
  const targetPixel = coordToPixel(
    targetCoord,
    boundingBox,
    { width: info.renderedWidth, height: info.renderedHeight },
    system
  );

  const pinTarget = $('#pin-target');
  pinTarget.style.left = `${adjustedOffsetX + targetPixel.px - 12}px`;
  pinTarget.style.top = `${adjustedOffsetY + targetPixel.py - 36}px`;

  setTimeout(() => {
    pinTarget.classList.remove('hidden');
    pinTarget.classList.add('anim-pinpoint-drop');
  }, 300);

  // Distance line
  const distLine = $('#distance-line') as unknown as SVGSVGElement;
  const line = distLine.querySelector('#dist-line') as SVGLineElement;
  distLine.style.position = 'absolute';
  distLine.style.top = '0';
  distLine.style.left = '0';
  distLine.style.width = '100%';
  distLine.style.height = '100%';
  distLine.style.pointerEvents = 'none';

  const x1 = adjustedOffsetX + userPixel.px;
  const y1 = adjustedOffsetY + userPixel.py;
  const x2 = adjustedOffsetX + targetPixel.px;
  const y2 = adjustedOffsetY + targetPixel.py;
  line.setAttribute('x1', String(x1));
  line.setAttribute('y1', String(y1));
  line.setAttribute('x2', String(x1));
  line.setAttribute('y2', String(y1));

  // Grow the end point towards the target. Animating stroke-dashoffset would
  // replace the dashed pattern and cut lines longer than the dash length.
  setTimeout(() => {
    distLine.classList.remove('hidden');
    const start = performance.now();
    const duration = 800;
    const step = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      line.setAttribute('x2', String(x1 + (x2 - x1) * eased));
      line.setAttribute('y2', String(y1 + (y2 - y1) * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, 600);
}

// ── Screen: Feedback (FEEDBACK) ──

export function renderFeedback(
  result: RoundResult,
  session: GameSession,
  onNext: () => void
): void {
  // Update HUD
  const hudPoints = document.querySelector('#hud-points');
  if (hudPoints) hudPoints.textContent = `${formatPoints(session.totalPoints)} ${t('game.points')}`;

  const hudPlace = document.querySelector('#hud-place');
  if (hudPlace) {
    if (!result.clicked) {
      hudPlace.textContent = t('game.timeout');
      hudPlace.classList.add('text-danger');
    } else {
      hudPlace.textContent = `${formatDistance(result.distanceKm, session.scenario!.distanceUnit)} ${t('game.distance_to')} ${getLocalized(result.place.name)}`;
    }
  }

  // Add next button
  const container = document.querySelector('#map-container');
  if (container) {
    const nextBtn = document.createElement('button');
    nextBtn.className = 'btn btn--primary btn--lg next-btn anim-fade-in-up';
    nextBtn.id = 'btn-next';
    nextBtn.innerHTML = `
      <span>+${formatPoints(result.points)} ${t('game.points').toLowerCase()}</span>
      <span class="next-btn__arrow">→ ${t('game.next')}</span>
    `;
    nextBtn.addEventListener('click', onNext);
    container.parentElement?.appendChild(nextBtn);
  }
}

// ── Screen: Results (RESULTS) ──

export function renderResults(
  session: GameSession,
  onReplay: () => void,
  onHome: () => void
): void {
  clearMain();
  const screen = createScreen('results');

  const maxPts = maxPossiblePoints(
    session.totalRounds,
    session.mode!.timePerRound,
    session.scenario!.maxDistance || 50
  );
  const level = getLevel(session.totalPoints, maxPts, session.scenario!.levels);
  const kmTotal = Math.round(session.totalKm);

  const shareData: ShareData = {
    scenario: getLocalized(session.scenario!.title),
    mode: getLocalized(session.mode!.label),
    score: session.totalPoints,
    level: getLocalized(level),
    kmTotal,
    distanceUnit: session.scenario!.distanceUnit,
    appUrl: window.location.origin,
  };

  screen.innerHTML = `
    <div class="results stagger">
      <div class="results__score anim-scale-in">
        <span class="results__emoji">🏆</span>
        <h1 class="results__points">${formatPoints(session.totalPoints)} ${t('results.title')}</h1>
        <p class="results__level">${t('results.level_prefix')} <strong>${getLocalized(level)}</strong></p>
        <p class="results__km text-muted">${kmTotal} ${t(session.scenario!.distanceUnit === 'mi' ? 'results.mi_suffix' : 'results.km_suffix')}</p>
      </div>

      <div class="results__share">
        ${canNativeShare() ? `
          <button class="btn btn--primary btn--lg" id="btn-share-native">
            📤 ${t('results.share')}
          </button>
        ` : ''}

        <div class="share-buttons">
          <button class="btn btn--icon share-btn share-btn--wa" id="btn-share-wa" title="WhatsApp">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </button>
          <button class="btn btn--icon share-btn share-btn--fb" id="btn-share-fb" title="Facebook">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </button>
          <button class="btn btn--icon share-btn share-btn--ig" id="btn-share-ig" title="Instagram">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
          </button>
          <button class="btn btn--icon share-btn share-btn--copy" id="btn-share-copy" title="${t('results.copy')}" aria-label="${t('results.copy')}">
            📋
          </button>
        </div>
      </div>

      <div class="results__actions">
        <button class="btn btn--secondary btn--lg" id="btn-replay">🔄 ${t('results.replay')}</button>
        <button class="btn btn--secondary btn--lg" id="btn-home">🏠 ${t('results.home')}</button>
      </div>
      
      <div class="ad-container" style="margin-top: 2rem; width: 100%; text-align: center;">
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-7741865405447617"
             data-ad-slot="5810682723"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      </div>
    </div>
  `;

  $('#main').appendChild(screen);
  pushAd();

  // Wire up share buttons (un solo evento `share` por clic, sea cual sea el boton)
  screen.querySelector('.results__share')?.addEventListener('click', (e) => {
    if ((e.target as Element).closest('button')) {
      track('share', { mode: 'classic', map: session.scenario!.id });
    }
  });
  const btnWa = screen.querySelector('#btn-share-wa');
  btnWa?.addEventListener('click', () => {
    shareWhatsApp(shareData);
    showToast(t('results.share_wa'));
  });

  const btnFb = screen.querySelector('#btn-share-fb');
  btnFb?.addEventListener('click', () => {
    shareFacebook(shareData);
    showToast(t('results.share_fb'));
  });

  const btnIg = screen.querySelector('#btn-share-ig');
  btnIg?.addEventListener('click', async () => {
    await shareInstagram(shareData);
    showToast(t('results.share_ig'));
  });

  const btnCopy = screen.querySelector('#btn-share-copy');
  btnCopy?.addEventListener('click', async () => {
    await copyToClipboard(shareData);
    showToast(t('results.share_copy'));
  });

  const btnNative = screen.querySelector('#btn-share-native');
  btnNative?.addEventListener('click', async () => {
    const shared = await shareNative(shareData);
    if (!shared) {
      await copyToClipboard(shareData);
      showToast(t('results.share_copy'));
    }
  });

  // Wire up actions
  screen.querySelector('#btn-replay')?.addEventListener('click', onReplay);
  screen.querySelector('#btn-home')?.addEventListener('click', onHome);
}

